// src/layouts/AssetLayout.jsx
import { useMemo, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import beatriceLogo from "../../assets/images/beatriceLogo.png";
import useAuthStore from "../../store/authStore";
import usecontactAccessStore from "../../store/contactAccessStore";
import { getContactMenuPermissions } from "../../features/adminContact/service/adminContact_service";
import useUserContextStore from "../../store/userContextStore";

const AssetHeader = ({ showMenu = true, showFooter = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [assetOpen, setAssetOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const assetDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const [permissions, setPermissions] = useState({
      assetVisible: false,
      overviewVisible: false,
      holdingsVisible: false,
      detailsVisible: false,
      statementVisible: false,
      taxDocsVisible: false,
      researchDocsVisible: false,
    });

  const user = useAuthStore((s) => s);
  const role = useAuthStore((s)=> s.role);
  const currentYear = new Date().getFullYear();

  const { contactId: contactIdParam } = useParams();
  const contactId = Number(atob(contactIdParam));

  const setMenuPermissionsList = usecontactAccessStore((state) => state.setMenuPermissionsList);
  const menuPermissions = usecontactAccessStore(s => s.menuPermissionsList);
  const setUserContext = useUserContextStore((state)=>state.setUserContext);
  const context = useUserContextStore((state)=> state.context);
  useEffect(()=>{
    if(context){
      setUserContext(null)
    }
  },[context])

  const fetchContactMenuPermissions = async() => {
    try{
      const response = await getContactMenuPermissions(contactId);
      setMenuPermissionsList(response.data);
    }
    catch{
      setMenuPermissionsList([]);
    }
  } 

  useEffect(()=>{
    const { showForbiddenAccess} = useAuthStore.getState();
    if(user.role != 1 && user?.user && user.user != contactId){
      showForbiddenAccess();
      window.location.href = "/";
    }
  }, []);

  
  useEffect(() => {
    if (!assetOpen && !profileOpen) return;

    const handlePointerDown = (e) => {
      const target = e.target;
      if (assetDropdownRef.current?.contains(target)) return;
      if (profileDropdownRef.current?.contains(target)) return;
      setAssetOpen(false);
      setProfileOpen(false);
    };

    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      setAssetOpen(false);
      setProfileOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("touchstart", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("touchstart", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [assetOpen, profileOpen]);

  
  useEffect(()=>{
    fetchContactMenuPermissions()
    const checkedMenus = {
      assetVisible: menuPermissions.filter(p => p.webMenuName == "Assets")[0]?.isChecked === 1,
      overviewVisible: menuPermissions.filter(p => p.webMenuName == "Overview")[0]?.isChecked === 1,
      holdingsVisible: menuPermissions.filter(p => p.webMenuName == "Holdings")[0]?.isChecked === 1,
      detailsVisible: menuPermissions.filter(p => p.webMenuName == "Details")[0]?.isChecked === 1,
      statementVisible: menuPermissions.filter(p => p.webMenuName == "Statements")[0]?.isChecked === 1,
      taxDocsVisible: menuPermissions.filter(p => p.webMenuName == "Documents")[0]?.isChecked === 1,
      researchDocsVisible: menuPermissions.filter(p => p.webMenuName == "Research Documents")[0]?.isChecked === 1,
    };
    setPermissions(checkedMenus);
    setLandingPage(checkedMenus);
  }, []);
  
  const setLandingPage = (checkedMenus) => {

    if (location.pathname.startsWith("/UserProfile")) return;
    const defaultAssetMenu =  menuPermissions.filter(p => p.isDefaultAssetSubMenu == true)[0]?.webMenuName;
    switch(defaultAssetMenu){
      case "Overview":
        !location.pathname.startsWith("/Asset/Overview") && go(`/Asset/Overview/${btoa(contactId)}`);
        break;
      case "Details":
        !location.pathname.startsWith("/Asset/Details") && go(`/Asset/Details/${btoa(contactId)}`);
        break;
      case "Holdings":
        !location.pathname.startsWith("/Asset/Holdings") && go(`/Asset/Holdings/${btoa(contactId)}`);
        break;
      default:
        checkedMenus.statementVisible ? go(`/Statements/${btoa(contactId)}`) 
        : checkedMenus.taxDocsVisible ? go(`/Documents/${btoa(contactId)}`) 
        : checkedMenus.researchDocsVisible ? go(`/Research/${btoa(contactId)}`) 
        : go(`/UserProfile/${btoa(contactId)}/password`)
    }
  }
  
  
  

  const go = (path) => {
    setAssetOpen(false);
    setProfileOpen(false);
    navigate(path);
  };

  const active = useMemo(() => {
    const p = location.pathname;
  return {
    assets: p.startsWith("/Asset"),
    overview: p.startsWith(`/Asset/Overview`),
    details: p.startsWith(`/Asset/Details`),
    holdings: p.startsWith(`/Asset/Holdings`),
    statements: p.startsWith(`/Statements`),
    documents: p.startsWith(`/Documents`),
    research: p.startsWith(`/Research`),
  };
}, [location.pathname, contactId]);

  
  const handleLogout = () => {
    if(role !== 1){
      localStorage.removeItem("SelectedInvestor");
      localStorage.removeItem("SelectedEntity");
      localStorage.removeItem("SelectedReportDate");
      navigate("/");
    }
    else if(role === 1){
      navigate("/AdminContact")
    }
  };

  return (
    <>
      <header className="new-head">
        <nav className="navbar navbar-default">
          <div className="container-fluid">
            <div className="navbar-header" style={{ marginLeft: 10 }}>
              <a className="navbar-brand" href="/">
                <img src={beatriceLogo} className="logo-icon" alt="Beatrice" />
              </a>
            </div>

            {showMenu && (
              <div className="collapse navbar-collapse">
                <ul className="nav navbar-nav navbar-right">
                  {permissions.assetVisible && (
                    <li className={active.assets ? "active" : ""} id="ContactProfile">
                      <div ref={assetDropdownRef} className={`dropdown ${assetOpen ? "open" : ""}`}>
                        <a
                          className="btn dropdown-toggle"
                          role="button"
                          onClick={() =>
                            setAssetOpen((v) => {
                              const next = !v;
                              if (next) setProfileOpen(false);
                              return next;
                            })
                          }
                        >
                          Assets <span className="caret" style={{ paddingTop: 4 }} />
                        </a>
                        <ul className="dropdown-menu" role="menu">
                          {permissions.overviewVisible && 
                            <li className={active.overview ? "active" : ""} style={active.overview ? {backgroundColor:"#10045a"} : {}}>
                              <a onClick={() => go(`/Asset/Overview/${btoa(contactId)}`)} style={active.overview ? {color:"white"} : {}}>Overview</a>
                            </li>
                          }
                          
                          {permissions.detailsVisible&& 
                            <li className={active.details ? "active" : ""} style={active.details ? {backgroundColor:"#10045a"} : {}}>
                              <a onClick={() => go(`/Asset/Details/${btoa(contactId)}`)} style={active.details ? {color:"white"} : {}}>Details</a>
                            </li>
                          }
                          {/* {permissions[5].isDefaultAssetSubMenu && go(`/Asset/Details/${contactId}`)} */}
                          {permissions.holdingsVisible  && 
                            <li className={active.holdings ? "active" : ""} style={active.holdings ? {backgroundColor:"#10045a"} : {}}>
                              <a onClick={() => go(`/Asset/Holdings/${btoa(contactId)}`)} style={active.holdings ? {color:"white"} : {}}>Holdings</a>
                            </li>
                          }
                          {/* {permissions[6].isDefaultAssetSubMenu && go(`/Asset/Holdings/${contactId}`)} */}
                        </ul>
                      </div>
                    </li>
                  )}

                  {permissions.statementVisible && (
                    <li className={active.statements ? "active" : ""}>
                      <a onClick={() => go(`/Statements/${btoa(contactId)}`)}>Statements</a>
                    </li>
                  )}

                  {permissions.taxDocsVisible && (
                    <li className={active.documents ? "active" : ""}>
                      <a onClick={() => go(`/Documents/${btoa(contactId)}`)}>Documents</a>
                    </li>
                  )}

                  {permissions.researchDocsVisible && (
                    <li className={active.research ? "active" : ""}>
                      <a onClick={() => go(`/Research/${btoa(contactId)}`)}>Research</a>
                    </li>
                  )}

                  <li id="ContactProfile">
                    <div ref={profileDropdownRef} className={`dropdown ${profileOpen ? "open" : ""}`}>
                      <a
                        className="btn dropdown-toggle"
                        role="button"
                        onClick={() =>
                          setProfileOpen((v) => {
                            const next = !v;
                            if (next) setAssetOpen(false);
                            return next;
                          })
                        }
                      >
                        My Profile <span className="caret" style={{ paddingTop: 4 }} />
                      </a>
                      <ul className="dropdown-menu" role="menu">
                        <li><a onClick={() => go(`/UserProfile/${btoa(contactId)}/password`)}>Change Password</a></li>
                        <li><a onClick={() => go(`/UserProfile/${btoa(contactId)}/security`)}>Change Secret Questions/Answers</a></li>
                        <li><a onClick={() => go(`/UserProfile/${btoa(contactId)}/email`)}>Change Email Preferences</a></li>
                        <li><a onClick={() => go(`/UserProfile/${btoa(contactId)}/mfa`)}>Configure MFA</a></li>
                      </ul>
                    </div>
                  </li>

                  <li onClick={handleLogout} style={{ cursor: "pointer", marginTop: -10 }}>
                    <a>
                      <i className="signouticon fas fa-sign-out-alt" aria-hidden="true" />
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </nav>
      </header>

      <div className="minHeightDiv">
        <Outlet />
      </div>

      {showFooter && (
        <footer className="col-md-12 new-footer emailFooter">
          <div style={{ paddingInline: "2%" }}>
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
              <div className="footer-links">
                <div><a href="https://www.beatriceadvisors.com/about" target="_blank" rel="noreferrer">About</a></div>
                <div><a href="https://www.beatriceadvisors.com/services" target="_blank" rel="noreferrer">Services</a></div>
                <div><a href="https://www.beatriceadvisors.com/news" target="_blank" rel="noreferrer">News</a></div>
                <div><a href="https://www.beatriceadvisors.com/contact" target="_blank" rel="noreferrer">Contact Us</a></div>
              </div>
              <div style={{ position: "relative" }}>
                <a href="/" target="_blank" rel="noreferrer">
                  <img src={beatriceLogo} className="logo-icon" alt="Beatrice" />
                </a>
              </div>
            </div>

            <div className="footer-legal-div">
              <div className="link-div">
                <a className="text-link-legal" href="https://www.beatriceadvisors.com/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a>
                <a className="text-link-legal" href="https://www.beatriceadvisors.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                <a className="text-link-legal" href="https://adviserinfo.sec.gov/firm/summary/158722" target="_blank" rel="noreferrer">Accessibility Statement</a>
              </div>
            </div>

            <div>
              <a className="text-link-legal">©{currentYear} Beatrice Advisors LP.</a>
            </div>
          </div>
        </footer>
      )}
    </>
  );
};

export default AssetHeader;
